import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Button, Card, Form, Input, Space, notification, Radio, Upload, Spin, Modal, Tooltip } from 'antd';
import { InboxOutlined } from '@ant-design/icons';

import filesize from 'filesize';
import path from 'path-browserify';

import * as actions from '../store/actions';
import * as sharedActions from '../../../shared/store/actions';

import { getArrayBuffer } from '../../../shared/utils';
import { fileOwnerTypes } from '../../../shared/constants/enums';

import { OriginalPdfFormsComponent } from '../../original-pdf-forms';
import { unsupportedFileTypeDefaultMsg, isValidFileBeforeUpload } from '../../../shared/utils/files/file-upload-accepts';

import MappingModal from '../mapping-modal';
import MappingTree from '../mapping-tree';

import '../styles.less';
import { isArray, isEmpty } from 'lodash';

const { Dragger } = Upload;

const customForm = ({ form, rootContainer, formLoading, onCancelClick, formId, redirectUrl, isNewForm }) => {
  const dispatch = useDispatch();

  const [pdfForm] = Form.useForm();

  const [formSaveCallback, setFormSaveCallback] = useState();
  const [formClearCallback, setFormClearCallback] = useState();
  const [newUserMappings, setNewUserMappings] = useState([]);
  const [uploadMethod, setUploadMethod] = useState(1);
  const [newFileLoading, setNewFileLoading] = useState(false);
  const [newForm, setNewForm] = useState();
  const [isNewUploadedForm, setIsNewUploadedForm] = useState(false);
  const [showMappedTree, setShowMappedTree] = useState(false);
  const [targetFocus, setTargetFocus] = useState();
  const [isDocumentChanged, setIsDocumentChanged] = useState(false);

  const updateFormLoading = useSelector((state) => state.customForm.updateFormLoading);
  const mappings = useSelector((state) => state.customForm.mappings);
  const mappingsLoading = useSelector((state) => state.customForm.mappingsLoading);
  const maxFileUploadSize = useSelector((state) => state.shared.maxFileUploadSize);
  const maxFileUploadSizeLoading = useSelector((state) => state.shared.maxFileUploadSizeLoading);
  const permissions = useSelector((state) => state.permissions.permissions);
  const permissionsLoading = useSelector((state) => state.permissions.permissionsLoading);
  const formName = useSelector((state) => state.shared.formName);
  const formNameLoading = useSelector((state) => state.shared.formNameLoading);
  const mappingObjectNames = useSelector((state) => state.customForm.mappingObjectNames);
  const customFormLoading = useSelector((state) => state.customForm.customFormLoading);
  const autoMappingResult = useSelector((state) => state.customForm.autoMappingResult);

  useEffect(() => {
    if (!isNewForm) {
      dispatch(actions.getMappings({ formId }));
      dispatch(sharedActions.getFormName(formId));
    }

    dispatch(sharedActions.getMaxFileUploadSize());
    dispatch(sharedActions.getPermissions());
    dispatch(actions.getMappingObjectNames());
  }, []);

  useEffect(() => {
    showMappedTree ? (document.body.style.overflow = 'hidden') : (document.body.style.overflow = 'unset');
  }, [showMappedTree]);

  useEffect(() => {
    if (form) {
      setNewForm(form);
    }
  }, [form]);

  useEffect(() => {
    pdfForm.setFieldsValue({ formName });
  }, [formName]);

  useEffect(() => {
    if (mappings) {
      setNewUserMappings(mappings.slice());
    }
  }, [mappings]);

  useEffect(() => {
    if (!isEmpty(autoMappingResult)) {
      Modal.info({
        getContainer: rootContainer,
        title: 'Map Fields results',
        content: (
          <div>
            <p>{autoMappingResult}</p>
          </div>
        ),
        onOk() {},
      });
    }
  }, [autoMappingResult]);

  useEffect(() => {
    setColorOfMappedFormFields(newUserMappings);
  }, [newUserMappings, form, formLoading, updateFormLoading]);

  useEffect(() => {
    const target = document.getElementById('viewer');
    if (!target) return;

    const observer = new MutationObserver(() => {
      setColorOfMappedFormFields(newUserMappings);
    });

    observer.observe(target, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [newUserMappings]);

  const setColorOfMappedFormFields = (newUserMappingsItems) => {
    if (isArray(newUserMappingsItems)) {
      newUserMappingsItems.forEach((x) => {
        if (x && !isEmpty(x.formFieldName)) {
          const elements = document.getElementsByName(x.formFieldName);

          if (elements) {
            const element = elements[0];

            if (element && element.style) {
              element.style.backgroundColor = '#e3c1c1';
            }
          }
        }
      });
    }
  };

  const setDefaultColorOfMappedFormFields = (oldUserMappingsItems) => {
    if (isArray(oldUserMappingsItems)) {
      oldUserMappingsItems.forEach((x) => {
        if (x && !isEmpty(x.formFieldName)) {
          const elements = document.getElementsByName(x.formFieldName);

          if (elements) {
            const element = elements[0];

            if (element && element.style) {
              element.style.backgroundColor = '';
            }
          }
        }
      });
    }
  };

  const onPdfLoadedEvent = () => {
    setColorOfMappedFormFields(newUserMappings);
  };

  const updateFormSource = (file) => {
    setNewForm(file);
    setNewUserMappings([]);
  };

  const onUpdate = () => {
    pdfForm.validateFields().then((formData) => {
      if (isNewForm) {
        if (!newForm) {
          notification.error({
            message: 'Error',
            top: 55,
            duration: 10,
            getContainer: rootContainer,
            description: 'The file data is empty.',
          });
        } else {
          updateForm(formData, newForm);
        }
      } else if (isNewUploadedForm || isDocumentChanged) {
        if (typeof formSaveCallback === 'function') {
          formSaveCallback().then((formDataArray) => updateForm(formData, formDataArray));
        }
      } else {
        updateForm(formData);
      }
    });
  };

  const updateForm = (formData, formDataArray) => {
    dispatch(
      actions.updateForm({
        formId,
        formDataArray: isNewUploadedForm || isDocumentChanged ? Array.from(new Uint8Array(formDataArray)) : undefined,
        ...formData,
        mappings: newUserMappings,
        isNewUploadedForm: isNewUploadedForm || isDocumentChanged,
      }),
    ).then(() => {
      notify('Your form has been update successfully');

      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    });
  };

  const notify = (description) => {
    notification.success({
      message: 'Success',
      top: 55,
      duration: 10,
      getContainer: rootContainer,
      description: description,
    });
  };

  const onUploadNewSourceMethodChanged = (e) => {
    setUploadMethod(e.target.value);
  };

  const uploadFile = (options) => {
    options.onSuccess(
      {
        name: 'name',
        status: 'done',
        url: 'test',
        thumbUrl: 'test',
      },
      options.file,
    );
  };

  const draggerProps = {
    name: 'file',
    multiple: false,
    customRequest: uploadFile,
    actions: '',
    maxCount: 1,
    onChange(info) {
      const { status } = info.file;

      switch (status) {
        case 'uploading':
          setNewFileLoading(true);
          break;

        case 'done':
          notification.success({
            message: 'Upload success',
            top: 55,
            duration: 10,
            getContainer: rootContainer,
            description: `${info.file.name} file uploaded successfully.`,
          });
          setNewFileLoading(false);
          pdfForm.setFieldsValue({ formName: path.parse(info.file.name).name });

          new Promise((resolve, reject) => {
            try {
              getArrayBuffer(info.file.originFileObj, (fileBytes) => resolve(fileBytes));
            } catch (error) {
              reject('Error load files. Error:', error);
            }
          }).then((fileBytes) => {
            updateFormSource(fileBytes);
            setIsNewUploadedForm(true);
          });
          break;

        case 'error':
          notification.error({
            message: 'Upload failed',
            top: 55,
            duration: 10,
            getContainer: rootContainer,
            description: `${info.file.name} file upload failed.`,
          });
          setNewFileLoading(false);
          break;

        default:
          setNewFileLoading(false);
          break;
      }
    },
    beforeUpload: (file) => {
      const isPDF = file.type === 'application/pdf';
      if (!isPDF) {
        notification.error({
          message: 'Upload failed',
          top: 55,
          duration: 10,
          getContainer: rootContainer,
          description: `${file.name} is not a pdf file`,
        });
      }

      const isNormalFileSize = file.size < maxFileUploadSize;
      if (!isNormalFileSize) {
        notification.error({
          message: 'Upload failed',
          top: 55,
          duration: 10,
          getContainer: rootContainer,
          description: `${file.name} is bigger than ${filesize(maxFileUploadSize)}`,
        });
      }

      let isValidFile = isValidFileBeforeUpload(file.name, file.type);
      if (!isValidFile) {
        notification.error({
          message: 'Upload failed',
          top: 55,
          duration: 10,
          getContainer: rootContainer,
          description: unsupportedFileTypeDefaultMsg,
        });
        return false;
      }

      return (isPDF && isNormalFileSize) || Upload.LIST_IGNORE;
    },
  };

  const onRowSelect = (fileBytes) => {
    updateFormSource(fileBytes);
    setIsNewUploadedForm(true);
  };

  const onShowMappedTreeClick = () => {
    setShowMappedTree(true);
  };

  const afterCloseModal = () => {
    if (targetFocus) {
      targetFocus.focus();

      targetFocus.classList.add('blink-background');

      const timer = setInterval(() => {
        clearInterval(timer);
        targetFocus.classList.remove('blink-background');
      }, 1000);

      setTargetFocus();
    }
  };

  const onCancel = () => {
    setShowMappedTree(false);
  };

  const onClearFormClick = () => {
    formClearCallback();
  };

  const onClearMappingsClick = () => {
    setDefaultColorOfMappedFormFields(newUserMappings);
    setNewUserMappings([]);
  };

  return (
    <div id="customForm">
      <Card title="PDF Form" size="small">
        <Form name="PDF_Form" form={pdfForm} size="small">
          <Spin spinning={formNameLoading}>
            <Form.Item
              label="Name"
              name="formName"
              required
              rules={[
                {
                  required: true,
                  message: 'This field is required.',
                  whitespace: true,
                },
                { max: 200, message: 'Name must be maximum 200 characters.' },
              ]}
            >
              <Input />
            </Form.Item>
          </Spin>

          <Form.Item label="Upload new Pdf Form">
            <Radio.Group onChange={onUploadNewSourceMethodChanged} defaultValue={1} disabled={permissionsLoading}>
              <Radio value={1}>Upload Your Own Form</Radio>
              <Radio value={2}>Select from Existing Forms</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>

        {uploadMethod === 1 ? (
          <Spin spinning={newFileLoading}>
            <Dragger {...draggerProps}>
              <p className="ant-upload-drag-icon">
                {' '}
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">Support for a single upload. Upload only PDF files and less than</p>
              {<Spin spinning={maxFileUploadSizeLoading}>{filesize(maxFileUploadSize)}</Spin>}
            </Dragger>
          </Spin>
        ) : (
          <OriginalPdfFormsComponent
            rootContainer={rootContainer}
            permissions={permissions}
            contactType={fileOwnerTypes.Insured}
            rowSelectionCallback={onRowSelect}
          />
        )}
      </Card>

      {!isNewForm ? (
        <Spin spinning={mappingsLoading || customFormLoading}>
          <Card title="PDF Data Mapping" size="small" className="card-data-mapping">
            <span>
              Click on a fillable field to set automatic pre-filling of available data like Agency name, phone; Policy number, eff./exp. date; Insured
              names etc. Consider that the position in the list of the objects starts from zero.
            </span>
            <Space className="data-mapping-buttons">
              <Button onClick={onShowMappedTreeClick}>Show mapped tree</Button>
              <Modal
                className="mapping-modal"
                title="Mapped Fields To Objects"
                open={showMappedTree}
                getContainer={rootContainer}
                centered
                width={800}
                onCancel={onCancel}
                onOk={onCancel}
                afterClose={afterCloseModal}
                footer={[
                  <Button key="submit" type="primary" onClick={onCancel}>
                    Ok
                  </Button>,
                ]}
              >
                <MappingTree
                  originalMappingObjects={mappingObjectNames}
                  userMappingObjects={newUserMappings}
                  setTargetFocus={setTargetFocus}
                  setShowMappingModal={setShowMappedTree}
                />
              </Modal>
            </Space>

            <MappingModal
              rootContainer={rootContainer}
              userMappingObjects={newUserMappings}
              setNewUserMappings={setNewUserMappings}
              loading={mappingsLoading}
              fileEditorLoading={!form || formLoading || updateFormLoading}
              fileBytes={newForm}
              registerFormSaveCallback={(callback) => setFormSaveCallback(() => callback)}
              onPdfLoadedEvent={onPdfLoadedEvent}
              registerFormClearCallback={(callback) => setFormClearCallback(() => callback)}
              setIsDocumentChanged={setIsDocumentChanged}
            />
          </Card>
        </Spin>
      ) : undefined}

      <div className="action-buttons">
        <Space>
          {!isNewForm ? (
            <Button onClick={onClearMappingsClick} loading={updateFormLoading || mappingsLoading || customFormLoading}>
              Clear mappings
            </Button>
          ) : null}
          {!isNewForm ? (
            <Button onClick={onClearFormClick} loading={updateFormLoading || mappingsLoading || customFormLoading}>
              Clear form
            </Button>
          ) : null}
          <Button onClick={onCancelClick} loading={updateFormLoading || mappingsLoading || customFormLoading}>
            Cancel
          </Button>
          <Button type="primary" onClick={onUpdate} loading={updateFormLoading || mappingsLoading || customFormLoading}>
            {isNewForm ? 'Save' : 'Update'}
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default customForm;
